import React, { useEffect, useState } from 'react';

import { FormikValues } from 'formik';

import {
  Question,
  Wrapper,
  UniqueBoxHeader,
  UniqueBoxLabel,
  InputContainer,
  InputHeader,
  CounterBadge,
  InputHeaderContent,
  CounterBadgeContent,
  IndexOrder,
} from './styles';
import { removeDuplicates } from 'utils/happyFunctions';
import Input from '../../../../../Input/Input';
import {
  exactWhite,
  black,
  stringToColour,
  lightGray,
} from 'utils/style/colors';
import Collapsible from '../../../../../Collapsible/Collapsible';
import { ITags, ITagView } from 'store/providers/providerTypes';
import {
  IChoiceView,
  IMultipleChoiceQuestionView,
  ISurveyItem,
  // ISurveyProperties,
  ISurveyResponseView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ICompany } from 'store/companies/companiesTypes';
import LocalIcon from '../../../../../../ui/LocalIcon';
import {
  convertDigitToProperFormat,
  convertNumbersToProperFormat,
} from '../../../../../../utils/number';
import { getDistinctiveColor } from '../../../../../../utils/style/getDistinctiveColor';
import { useTranslation } from 'react-i18next';
import { languageDirection } from '../../../../../../i18n';
import { getValidChoices } from '../../SurveyResponseViewer/SurveyResponseViewer';

interface IUniqueBoxProps {
  tags: ITags;
  values: FormikValues;
  status: any;
  question: IMultipleChoiceQuestionView;
  hideCounts: boolean;
  onChangeValues: (
    e: React.ChangeEvent<HTMLInputElement>,
    level?: number
  ) => void;
  onChangeOptions: (id: number, options: number[]) => void;
  showResponseForm: boolean;
  currentUserCompanyRoles: number[];
  company?: ICompany;
  surveyItem?: ISurveyItem;
  response?: ISurveyResponseView;
  userRoles?: number[];
  thisUserId?: number;
  survey?: ISurveyView;
}

const UniqueBox: React.FC<IUniqueBoxProps> = ({
  tags,
  status,
  values,
  question,
  hideCounts,
  onChangeValues,
  onChangeOptions,
  showResponseForm,
  currentUserCompanyRoles,
  company,
  surveyItem,
  response,
  userRoles,
  thisUserId,
  survey,
}) => {
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation('survey');
  const side = languageDirection() === 'rtl' ? 'left' : 'right';
  let choices: number[] = [];
  const companyMemberRoles = Object.keys(
    company?.members?.[thisUserId || 0]?.roles || {}
  );
  const relatedPermissions = [
    `U${thisUserId}`,
    ...(companyMemberRoles?.map((r) => `R${r}`) || []),
  ];
  const notShowIndex =
    survey?.fields
      ?.find((item) => {
        return item.name === 'conf_nonNumbering';
      })
      ?.value.toLowerCase() === 'true';

  const permissionScheme = surveyItem?.changeDataToken
    ? []
    : showResponseForm //|| !surveyItem?.reportToken
      ? question.fillPermissionSchemeList || []
      : question.reportPermissionSchemeList || [];
  let activeRelatedPermissions = surveyItem?.changeDataToken
    ? []
    : showResponseForm || !surveyItem?.reportToken
      ? [
          ...(surveyItem?.activePermissions?.effectiveReadPermission ?? []),
          ...(surveyItem?.activePermissions?.effectiveReportPermission ?? []),
        ]?.filter((it) => relatedPermissions.includes(it)) || []
      : surveyItem?.activePermissions?.effectiveReportPermission?.filter((it) =>
          relatedPermissions.includes(it)
        ) || [];
  if (!thisUserId) activeRelatedPermissions = ['direct']; // direct access (non-logged in)  users bypass permission scheme anyway
  const isEditor = !!surveyItem?.editToken;
  const protectEditFromThisUser =
    (question.protection && !surveyItem?.changeDataToken) ||
    ((question.editableWith?.length ?? 0) > 0 &&
      !userRoles?.find((e) => question.editableWith?.includes('R' + e)) &&
      !(
        question.editableWith?.includes('@senderWhileSending') &&
        (!response || (response.draft && response.userId === thisUserId))
      ) &&
      !(
        question.editableWith?.includes('@senderWhileUpdating') &&
        response &&
        !response.draft &&
        response.userId === thisUserId
      ) &&
      !isEditor);

  const validChoices = company
    ? getValidChoices(
        company,
        permissionScheme,
        currentUserCompanyRoles,
        activeRelatedPermissions,
        question.readFromItemTag && surveyItem?.upstreamItemId
          ? surveyItem?.tags.map((it) => +it.id)
          : undefined
      )
    : undefined;

  if (question.useTags && tags) {
    // TODO: parent tags could be null instead of array
    if (question.parentTagChoices) {
      question.parentTagChoices.forEach((parentId: number) => {
        // TODO should check for null
        Object.values(tags)
          .filter(
            (tag: ITagView) =>
              tag.parentId === parentId &&
              !tag.deleted &&
              !question.excludedTagChoices?.includes(Number(tag.id)) &&
              (validChoices?.includes(Number(tag.id)) ?? true)
          )
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
          .map((item: ITagView) => choices.push(item.id as number));
      });
    }
    if (question.tagChoices) {
      question.tagChoices.forEach((tagChoice: number) => {
        if (!validChoices || validChoices.includes(tagChoice))
          choices.push(tagChoice);
      });
    }
    choices = removeDuplicates(choices);
  }

  useEffect(() => {
    if (question.useTags)
      if (
        choices?.length <= 1 &&
        // question.useTags &&
        !response?.id &&
        // showResponseForm &&
        !(values[question.id] * 1)
      ) {
        const choiceValue = choices[0] || '-';
        onChangeValues({
          target: {
            name: question.id.toString(),
            value: choiceValue.toString(),
            checked: true,
            // dataset comes from UniqueBox & PeriodTypeSelector's inputs
            dataset: {
              label: `${question.title} ${tags?.[choices[0]]?.text || '-'}`,
              order: question.order.toString(),
            },
          } as any,
        } as any);
      } else if (validChoices && !showResponseForm) {
        onChangeOptions(question.id, validChoices);
      }
  }, [choices.length, showResponseForm]);
  const isQuestionAnswered = showResponseForm && !!values[question.id];
  const rawValue = values?.[question.id];

  const selectedValue = question.useTags
    ? rawValue &&
      tags &&
      typeof rawValue === 'string' &&
      Number(rawValue.split(',')[0]) &&
      tags[Number(rawValue.split(',')[0])]?.text
    : question.choiceList.find((it) => rawValue === it.value)?.text;
  const [collapsed, setCollapsed] = useState(!!selectedValue);
  useEffect(() => {
    setCollapsed(false);
  }, [showResponseForm]);
  if (
    (choices.length === 1 && !surveyItem?.changeDataToken) ||
    (question.readFromItemTag && surveyItem?.upstreamItemId)
  )
    return <div />;

  const selectedTagsInDepth =
    typeof values?.[question.id] === 'string'
      ? values[question.id].split(',')
      : [];
  const maxDepth = question.goDeepInTags ?? 0;
  const nextDepth = selectedTagsInDepth?.length;

  const renderTagOptions = (parentTagId: number, depth = 1) => {
    const tagOptions = Object.values(tags)
      .filter(
        (tag: ITagView) =>
          !tag.deleted &&
          !question.excludedTagChoices?.includes(Number(tag.id)) &&
          tag.parentId === parentTagId
      )
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((item: ITagView) => item.id as number);
    return tagOptions.map((tagChoice) => {
      return (
        <InputContainer key={tagChoice}>
          <InputHeader
            hideCounts={hideCounts}
            checked={
              values && Number(selectedTagsInDepth?.[depth]) === tagChoice
            }
            color={
              hideCounts
                ? stringToColour(
                    question.title,
                    1,
                    null,
                    undefined,
                    1,
                    1,
                    undefined,
                    undefined,
                    true
                  )
                : getDistinctiveColor(
                    question,
                    status && status[question.id],
                    (status &&
                      status[question.id] &&
                      status[question.id][tagChoice]) ||
                      0
                  )
            }
          >
            <InputHeaderContent
              checked={
                values && Number(selectedTagsInDepth[depth]) === tagChoice
              }
            >
              <input
                id={`${surveyItem?.id}-${question.id}-${tagChoice}`}
                type="checkbox"
                name={question.id.toString()}
                value={tagChoice}
                onChange={(e) => {
                  if (showResponseForm && e.target.checked) setCollapsed(true);
                  onChangeValues(e, depth);
                }}
                data-order={question.order}
                data-label={`${question.title} ${tags[tagChoice]?.text}`}
                disabled={
                  choices?.length === 1 &&
                  (validChoices?.length || 0) > 0 &&
                  values &&
                  Number(selectedTagsInDepth[depth]) === tagChoice
                }
                checked={
                  values && Number(selectedTagsInDepth[depth]) === tagChoice
                }
              />
              <UniqueBoxLabel
                htmlFor={`${surveyItem?.id}-${question.id}-${tagChoice}`}
                isActive={!isQuestionAnswered}
              >
                {tags[tagChoice]?.text}
              </UniqueBoxLabel>
            </InputHeaderContent>
          </InputHeader>
          {!hideCounts && (
            <CounterBadge>
              <CounterBadgeContent
                checked={
                  values && Number(selectedTagsInDepth[depth]) === tagChoice
                }
                color={getDistinctiveColor(
                  question,
                  status && status[question.id],
                  (status &&
                    status[question.id] &&
                    status[question.id][tagChoice]) ||
                    0
                )}
              >
                {convertNumbersToProperFormat(
                  Number(
                    status &&
                      status[question.id] &&
                      status[question.id][tagChoice]
                  ) || 0
                )}
              </CounterBadgeContent>
            </CounterBadge>
          )}
        </InputContainer>
      );
    });
  };

  return (
    <Question isActive={isQuestionAnswered}>
      <Collapsible
        headerWrapperPadding="0.5rem 1rem"
        headerContentColor={black}
        onClose={() => setCollapsed(false)}
        onOpen={() => setCollapsed(true)}
        isOpen={protectEditFromThisUser ? false : !collapsed}
        defaultOpen={protectEditFromThisUser ? false : !collapsed}
        headerBackgroundColor={exactWhite}
        arrowLocation="start"
        header={
          <UniqueBoxHeader
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingInlineStart: '0.4rem',
              }}
            >
              {!notShowIndex && (
                <IndexOrder>
                  {convertDigitToProperFormat(
                    question.indexOrder?.toString() || ''
                  )}
                </IndexOrder>
              )}
              <span>{question.title + ': '} </span>
              {selectedValue && (showResponseForm || collapsed) && (
                <span style={{ margin: '5px' }}>{selectedValue}</span>
              )}
            </span>
            {!protectEditFromThisUser && !collapsed && (
              <Input
                id="search"
                type="text"
                name="temp"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  height: '2.5rem',
                  color: black,
                }}
                autoComplete="off"
                backgroundColor={'initial'}
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed(false);
                }}
                placeholder={t('search')}
                label={
                  <LocalIcon
                    iconName="search-normal.svg"
                    color={black}
                    type="sm"
                  />
                }
                labelStyle={{
                  position: 'absolute',
                  top: 0,
                  [side]: 0,
                  padding: '1.2rem',
                }}
              />
            )}
          </UniqueBoxHeader>
        }
        customBodyStyle={{ padding: '0px' }}
        customHeaderStyle={{ padding: '0px', flexGrow: 1 }}
      >
        <Wrapper>
          <>
            {question.useTags && tags
              ? choices
                  .filter(
                    (tagChoice) =>
                      !searchText ||
                      tags[tagChoice]?.text?.includes(searchText) ||
                      (values && values[question.id] * 1 === tagChoice)
                  )
                  .map((tagChoice) => {
                    return (
                      <InputContainer key={tagChoice}>
                        <InputHeader
                          hideCounts={hideCounts}
                          checked={
                            values &&
                            Number(selectedTagsInDepth?.at(0)) === tagChoice
                          }
                          color={
                            hideCounts
                              ? stringToColour(
                                  question.title,
                                  1,
                                  null,
                                  undefined,
                                  1,
                                  1,
                                  undefined,
                                  undefined,
                                  true
                                )
                              : getDistinctiveColor(
                                  question,
                                  status && status[question.id],
                                  (status &&
                                    status[question.id] &&
                                    status[question.id][tagChoice]) ||
                                    0
                                )
                          }
                        >
                          <InputHeaderContent
                            checked={
                              values &&
                              Number(selectedTagsInDepth?.at(0)) === tagChoice
                            }
                          >
                            <input
                              id={`${surveyItem?.id}-${question.id}-${tagChoice}`}
                              type="checkbox"
                              name={question.id.toString()}
                              value={tagChoice}
                              onChange={(e) => {
                                if (showResponseForm && e.target.checked)
                                  setCollapsed(true);
                                onChangeValues(e);
                              }}
                              data-order={question.order}
                              data-label={`${question.title} ${tags[tagChoice]?.text}`}
                              disabled={
                                choices?.length === 1 &&
                                (validChoices?.length || 0) > 0 &&
                                values &&
                                Number(selectedTagsInDepth?.at(0)) === tagChoice
                              }
                              checked={
                                values &&
                                selectedTagsInDepth?.at(0) * 1 === tagChoice
                              }
                            />
                            <UniqueBoxLabel
                              htmlFor={`${surveyItem?.id}-${question.id}-${tagChoice}`}
                              isActive={!isQuestionAnswered}
                            >
                              {tags[tagChoice]?.text}
                            </UniqueBoxLabel>
                          </InputHeaderContent>
                        </InputHeader>
                        {!hideCounts && (
                          <CounterBadge>
                            <CounterBadgeContent
                              checked={
                                values &&
                                Number(selectedTagsInDepth?.at(0)) === tagChoice
                              }
                              color={getDistinctiveColor(
                                question,
                                status && status[question.id],
                                (status &&
                                  status[question.id] &&
                                  status[question.id][tagChoice]) ||
                                  0
                              )}
                            >
                              {convertNumbersToProperFormat(
                                +(
                                  status &&
                                  status[question.id] &&
                                  status[question.id][tagChoice]
                                ) || 0
                              )}
                            </CounterBadgeContent>
                          </CounterBadge>
                        )}
                      </InputContainer>
                    );
                  })
              : question.choiceList
                  .filter(
                    (choice: IChoiceView) =>
                      !searchText ||
                      choice.text.includes(searchText) ||
                      (values && values[question.id] === choice.value)
                  )
                  .map((choice: IChoiceView) => {
                    return (
                      <InputContainer
                        key={`${surveyItem?.id}-${question.id}-${choice.value}`}
                      >
                        <InputHeader
                          checked={
                            values && values[question.id] === choice.value
                          }
                          hideCounts={hideCounts}
                          color={
                            hideCounts
                              ? stringToColour(
                                  question.title,
                                  1,
                                  null,
                                  undefined,
                                  1,
                                  1,
                                  undefined,
                                  undefined,
                                  true
                                )
                              : getDistinctiveColor(
                                  question,
                                  status && status[question.id],
                                  (status &&
                                    status[question.id] &&
                                    status[question.id][choice.value]) ||
                                    0
                                )
                          }
                        >
                          <InputHeaderContent
                            checked={
                              values && values[question.id] === choice.value
                            }
                          >
                            <input
                              id={`${surveyItem?.id}-${question.id}-${choice.value}`}
                              name={question.id.toString()}
                              type="checkbox"
                              value={choice.value}
                              onChange={(e) => {
                                if (showResponseForm && e.target.checked)
                                  setCollapsed(true);
                                onChangeValues(e);
                              }}
                              data-order={question.order}
                              data-label={`${question.title} ${choice.text}`}
                              checked={
                                values && values[question.id] === choice.value
                              }
                            />
                            <UniqueBoxLabel
                              checked={
                                values && values[question.id] === choice.value
                              }
                              htmlFor={`${surveyItem?.id}-${question.id}-${choice.value}`}
                              isActive={!isQuestionAnswered}
                            >
                              {choice.text}
                            </UniqueBoxLabel>
                          </InputHeaderContent>
                        </InputHeader>
                        {!hideCounts && (
                          <CounterBadge>
                            <CounterBadgeContent
                              checked={
                                values && values[question.id] === choice.value
                              }
                              color={getDistinctiveColor(
                                question,
                                status && status[question.id],
                                (status &&
                                  status[question.id] &&
                                  status[question.id][choice.value]) ||
                                  0
                              )}
                            >
                              {convertNumbersToProperFormat(
                                +(
                                  status &&
                                  status[question.id] &&
                                  status[question.id][choice.value]
                                ) || 0
                              )}
                            </CounterBadgeContent>
                          </CounterBadge>
                        )}
                      </InputContainer>
                    );
                  })}
          </>
          <>
            {question.useTags &&
              tags &&
              maxDepth > 0 &&
              nextDepth &&
              selectedTagsInDepth?.map((tagId: string, index: number) => {
                return index < maxDepth ? (
                  <Wrapper
                    key={tagId}
                    style={{
                      borderTop: index < maxDepth && `1px solid ${lightGray}`,
                    }}
                  >
                    {renderTagOptions(Number(tagId), index + 1)}
                  </Wrapper>
                ) : (
                  <></>
                );
              })}
          </>
        </Wrapper>
      </Collapsible>
    </Question>
  );
};

export default UniqueBox;
